import _ from 'underscore';
import { odata } from '@bingads-webui/http-util';
import { Callback } from '@bingads-webui/http-util/src/types';

import { IServerContext, RecommendationError } from '../../types';
import { ErrorCode } from './constants';
import { getCommonOdataOptions } from './utils';
import { ITaskPoller, TaskPoller } from './TaskPoller';
import { CopilotRecommendationServiceError } from './copilot-recommendation-service-error';

const TASK_STATUS = {
  INPROGRESS: 'InProgress',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};

const DEFAULT_MAX_FAIL_RETRY_COUNT = 3;
const DEFAULT_MAX_RETRY_COUNT = 10;
const DEFAULT_POLL_INTERVAL = 3000;

interface IODataOptions {
  contentType: string,
  host: string,
  path: string,
  oDataToken: string,
  oDataTokenType?: string,
}

function generateUrl(cid: number, aid: number, campaignId: number, oDataFunction: string) {
  return `Customers(${cid})/Accounts(${aid})/Campaigns(${campaignId})/Default.${oDataFunction}`;
}

function generateAccountLevelUrl(cid: number, aid: number, methodName: string) {
  return `Customers(${cid})/Accounts(${aid})/Default.${methodName}`;
}

function generateTaskResultUrl(cid: number, aid: number, campaignId: number, methodName: string, taskId: string) {
  return `${generateUrl(cid, aid, campaignId, methodName)}Result(TaskId='${taskId}')`;
}

function createRecommendationTask(cid: number, aid: number, campaignId: number, api: string, oDataOption: IODataOptions, data: any, callback?: Callback) {
  const url = generateUrl(cid, aid, campaignId, api);

  const options = {
    ...oDataOption,
    url,
    data,
  };
  return odata.post(options, callback);
}

function getRecommendationTaskResult(cid: number, aid: number, campaignId: number, oDataFunction: string, taskId: string, oDataOption: IODataOptions) {
  const url = generateTaskResultUrl(cid, aid, campaignId, oDataFunction, taskId);
  const options = _.extend(
    {},
    oDataOption,
    {
      url,
    },
  );
  return odata.get(options);
}

function mapError(error: {
  Code: string,
}) {
  const errorCode = error.Code;

  switch (errorCode) {
    case ErrorCode.TOO_MANY_REQUESTS:
      return RecommendationError.TOO_MANY_REQUESTS;
    case ErrorCode.INTERNAL_SERVER_ERROR:
    case ErrorCode.BAD_REQUEST:
      return RecommendationError.NO_RECOMMENDATION;
    default:
      return RecommendationError.NO_RECOMMENDATION;
  }
}

export interface ICopilotRecommendationProps {
  serverContext: IServerContext,
  maxFailRetryCount?: number,
  maxRetryCount?: number,
  pollInterval?: number,
}

export interface ICopilotRecommendationService {
  getRecommendation: (campaignId: number | undefined, methodName: string, payload: any) => Promise<any>,
  stopPolling: (methodName: string) => void,
  post: (campaignId: number | undefined, methodName: string, payload: any) => Promise<any>,
}

interface TaskData {
  [key: string]: { taskPoller: ITaskPoller | undefined, startTime: number };
}

export const CopilotRecommendationService = (props: ICopilotRecommendationProps): ICopilotRecommendationService => {
  const maxFailRetryCount = props.maxFailRetryCount || DEFAULT_MAX_FAIL_RETRY_COUNT;
  const maxRetryCount = props.maxRetryCount || DEFAULT_MAX_RETRY_COUNT;
  const pollInterval = props.pollInterval || DEFAULT_POLL_INTERVAL;

  const {
    cid,
    aid,
    host,
    path,
    token,
    tokenType,
  } = getCommonOdataOptions(props.serverContext);

  const oDataOption = {
    contentType: 'application/json',
    host,
    path,
    oDataToken: token,
    oDataTokenType: tokenType,
  };

  const runningTasks: TaskData = {};

  const stopPolling = (methodName: string) => {
    if (runningTasks[methodName]) {
      runningTasks[methodName].taskPoller?.stop();
      delete runningTasks[methodName];
    }
  };

  const getRecommendation = (campaignId: number | undefined, oDataFunction: string, payload: any) => {
    stopPolling(oDataFunction);
    const startTime = Date.now();
    runningTasks[oDataFunction] = { startTime, taskPoller: undefined };

    return new Promise((resolve, reject) => {
      if (campaignId == null) {
        reject(new Error(RecommendationError.CAMPAIGN_ID_REQUIRED));
        return;
      }

      // create a callback to capture jqXHR object with request id
      let jqXHR: JQuery.jqXHR | undefined;
      const callback: Callback = (_errorDetails, _response, _statusText, jqueryXmlHttpRequest) => {
        jqXHR = jqueryXmlHttpRequest;
      };

      createRecommendationTask(cid, aid, campaignId, oDataFunction, oDataOption, payload, callback).then((res: any) => {
        const {
          Data: data,
          Errors: errors,
        } = res;

        let taskId: string = '';

        if (data) { // data could be task id or task object, this will be removed after schema unified
          if (typeof data === 'string') {
            taskId = data;
          } else {
            if (Object.prototype.hasOwnProperty.call(data, 'TaskId')) {
              taskId = data.TaskId;
            }
            if (Object.prototype.hasOwnProperty.call(data, 'Status')) {
              if (data.Status === TASK_STATUS.COMPLETED) {
                const recommendation = _.omit(data, 'TaskId', 'Message', 'Progress', 'Status');
                resolve(recommendation);
                return;
              }
            }
          }
        }

        let retryCount = 0;
        let failRetryCount = 0;

        if (errors && !_.isEmpty(errors)) {
          reject(new CopilotRecommendationServiceError(mapError(errors[0]), oDataFunction, jqXHR));
          return;
        }

        // if task is not completed yet, taskId needs to be available so we can poll for status
        if (data.Status !== TASK_STATUS.COMPLETED && !taskId) {
          reject(new CopilotRecommendationServiceError(RecommendationError.RECOMMENDATION_FAILED, oDataFunction, jqXHR));
          return;
        }

        const pollStatus = () => getRecommendationTaskResult(cid, aid, campaignId, oDataFunction, taskId, oDataOption).then((result: any) => {
          if (result && result.Errors && !_.isEmpty(result.Errors)) {
            reject(new Error(mapError(result.Errors[0])));
            return { isFinished: true };
          }

          const status = result && result.Data && result.Data.Status;
          const isFinished = status === TASK_STATUS.COMPLETED;
          if (isFinished) {
            const recommendation = _.omit(result.Data, 'TaskId', 'Message', 'Progress', 'Status');
            resolve(recommendation);
          }

          retryCount += 1;
          if (retryCount >= maxRetryCount) {
            reject(new Error(RecommendationError.RECOMMENDATION_FAILED));
            return { isFinished: true };
          }

          return { isFinished };
        }).catch(() => {
          failRetryCount += 1;
          if (failRetryCount >= maxFailRetryCount) {
            reject(new CopilotRecommendationServiceError(RecommendationError.RECOMMENDATION_FAILED, oDataFunction, jqXHR));
            return { isFinished: true };
          }

          return { isFinished: false };
        });

        if (!runningTasks[oDataFunction]) {
          reject(new CopilotRecommendationServiceError(RecommendationError.RECOMMENDATION_CANCELLED, oDataFunction, jqXHR));
          return;
        }

        if (runningTasks[oDataFunction].startTime > startTime) {
          // new task is created, ignore the old one
          reject(new CopilotRecommendationServiceError(RecommendationError.NEW_RECOMMENDATION_CREATED, oDataFunction, jqXHR));
          return;
        }

        stopPolling(oDataFunction);
        // start polling and save the taskPoller
        const taskPoller = TaskPoller({ task: pollStatus, interval: pollInterval });
        taskPoller.start();
        runningTasks[oDataFunction] = {
          startTime,
          taskPoller,
        };
      }).catch(() => {
        reject(new CopilotRecommendationServiceError(RecommendationError.RECOMMENDATION_FAILED, oDataFunction, jqXHR));
      });
    });
  };

  // these recommendations are not task based, these are basic get/post requests
  const post = (campaignId: number | undefined, oDataFunction: string, payload: any) => new Promise((resolve, reject) => {
    if (campaignId == null) {
      reject(new Error(RecommendationError.CAMPAIGN_ID_REQUIRED));
      return;
    }

    const url = generateAccountLevelUrl(cid, aid, oDataFunction);

    const options = {
      ...oDataOption,
      url,
      data: payload,
    };

    resolve(odata.post(options));
  });

  return {
    getRecommendation,
    post,
    stopPolling,
  };
};
